import {useEffect, useState} from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { InstagramOutlined, MailOutlined, FilterOutlined, DownOutlined } from '@ant-design/icons';
import { volcano } from '@ant-design/colors';
import './App.css';
import imageCompression from 'browser-image-compression';
import * as exifr from 'exifr';
import MorphingBackground from './components/MorphingBackground';
import logo from './assets/logo.png';

import { Button, Badge, Layout, Row, Dropdown, Space, Card, Col, message, Spin } from 'antd';
import FilterModal from './components/FilterModal';
import PhotoExplorer from './components/PhotoExplorer';
import UploadDropzone from './components/BulkUpload/UploadDropzone';
import MetadataTabs from './components/BulkUpload/UploadTabs';
const { Header, Content, Footer } = Layout;

const API_URL = process.env.REACT_APP_API_URL || '/api';

function HomePage({ setBackgroundImages, filterOptions}) {
  return (
        <Row style={{height: "100%"}}>
          <PhotoExplorer backgroundSetter={setBackgroundImages} filterOptions={filterOptions}/>
        </Row>
  );
}

function BulkUploadPage({auth}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [globalMetadata, setGlobalMetadata] = useState({});

  async function extractExif(file) {
    const raw = await exifr.parse(file, {
      ifd0: true,
      exif: true,
      gps: false,
      interop: false,
      xmp: false,
      translateValues: true,
    })

    return {
      date_taken: raw.DateTimeOriginal || null,
      camera_make: raw.Make || null,
      camera_model: raw.Model || null,
      lens_model: raw.LensModel || null,
      exposure_time: raw.ExposureTime
        ? `1/${Math.round(1 / raw.ExposureTime)}`
        : null,
      exposure_fstop: raw.FNumber,
      exposure_iso: raw.ISO || null,
      focal_absolute: raw.FocalLength,
      exposure_flash: raw.Flash || null,
    };
  }

  async function compressPreserveExif(file) {
    const options = {
      maxWidthOrHeight: 6000,
      maxSizeMB: 12,
      useWebWorker: true,
      initialQuality: 0.85,
      exifOrientation: true, // 🔑 preserve orientation
      preserveExif: true, // 🔑 preserve EXIF data
    };
    const compressedBlob = await imageCompression(file, options);

    // Preserve original filename
    return new File([compressedBlob], file.name, {
      type: compressedBlob.type || file.type,
      lastModified: file.lastModified,
    });
  }

  if (!auth || !auth.authenticated) {
    return (
      <Row style={{height: "100%"}} justify="center" align="middle">
        <Card 
          title="Access Denied"
          style={{ 
            width: 400, 
            textAlign: 'center', 
            padding: '20px',
            backgroundColor: 'rgba(30, 30, 30, 0.8)',
            border: '1px solid #fa0',
            boxShadow: '0 0 10px #000'
          }}
          styles={{
            title: { color: '#fa0' },
          }}
        >
          <div style={{ padding: '20px', color: '#fff' }}>
            <p>How did you even get here? This thing is just for me to manage the site. Go away ;)</p>
            <Button 
              type="primary" 
              href="/"
            >
              Go back
            </Button>
          </div>
        </Card>
      </Row>
    );
  }

  function getCSRFToken() {
    const name = "csrftoken=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const parts = decodedCookie.split(";");

    for (let part of parts) {
      part = part.trim();
      if (part.startsWith(name)) {
        return part.substring(name.length);
      }
    }
    return null;
  }

  async function submitBulkUpload(files, globalMetadata) {
    let hadErrors = false;

    for (let i = 0; i < files.length; i++) {
      setFiles(prev =>
        prev.map((f, idx) =>
          idx === i ? { ...f, status: "uploading" } : f
        )
      );
  
      try {
        await uploadSingleFile({
          item: files[i],
          globalMetadata,
          onProgress: (progress) => {
            setFiles(prev =>
              prev.map((f, idx) =>
                idx === i ? { ...f, progress } : f
              )
            );
          },
        });
  
        setFiles(prev =>
          prev.map((f, idx) =>
            idx === i
              ? { ...f, status: "done", progress: 100 }
              : f
          )
        );
      } catch (err) {
        hadErrors = true;
        console.error("Upload failed:", err);

        setFiles(prev =>
          prev.map((f, idx) =>
            idx === i ? { 
              ...f, 
              status: "error" ,
              error:
                err?.response?.failed?.[0]?.error ??
                "Server rejected file"
            } : f
          )
        );
      }
    }
    return {hadErrors}
  }

  function mergeMetadata(globalMeta, itemMeta) {
    return {
      tags: itemMeta.tags ?? globalMeta.tags ?? [],
      subject: itemMeta.subject ?? globalMeta.subject ?? null,
      location: itemMeta.location ?? globalMeta.location ?? null,
      galleries: itemMeta.galleries ?? globalMeta.galleries ?? [],
    };
  }

  function uploadSingleFile({ item, globalMetadata, onProgress }) {
    if (!item || !item.compressedSize > 14 * 1024 * 1024) {
      return Promise.reject("File is too large after compression (>14MB)");
    }
    return new Promise((resolve, reject) => {
      const formData = new FormData();
  
      const metadata = mergeMetadata(globalMetadata, item.metadata || {});

      console.table(item.exif);
  
      formData.append("files", item.file);
      formData.append(
        "payload",
        JSON.stringify({
          items: [
            {
              filename: item.file.name,
              metadata,
              exif: item.exif || {}
            },
          ],
        })
      );
  
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/photos/bulk-upload/");
      xhr.withCredentials = true;
      xhr.setRequestHeader("X-CSRFToken", getCSRFToken());
  
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };
  
      xhr.onload = () => {
        let response;
        try {
          response = JSON.parse(xhr.responseText);
        } catch {
          reject("Invalid JSON response");
          return;
        }
      
        if (xhr.status >= 200 && xhr.status < 300) {
          if (response.created && response.created.length === 1) {
            resolve(response);
          } else {
            reject({
              type: "server",
              response,
            });
          }
        } else {
          reject({
            type: "http",
            status: xhr.status,
            response,
          });
        }
      };
  
      xhr.onerror = () => reject("Network error");
  
      xhr.send(formData);
    });
  }

  const startCompression = async (item) => {
    try {
      const exif = await extractExif(item.file);
      const compressedFile = await compressPreserveExif(item.file);
  
      setFiles(prev =>
        prev.map(f =>
          f.id === item.id
            ? {
                ...f,
                file: compressedFile,
                compressedSize: compressedFile.size,
                status: "pending",
                exif,
              }
            : f
        )
      );
    } catch (err) {
      setFiles(prev =>
        prev.map(f =>
          f.id === item.id
            ? {
                ...f,
                status: "error",
                error: "Compression or EXIF extraction failed",
              }
            : f
        )
      );
    }
  };

  return (
      <Row style={{height: "100%"}} justify="center" align="middle">
        <div style={{ width: "90%", height: "90%"}}>
        <Card 
          title="Bulk Upload Tool"
          style={{ 
            textAlign: 'center', 
            padding: '20px',
            backgroundColor: 'rgba(30, 30, 30, 0.8)',
            border: '1px solid #333',
            boxShadow: '0 0 10px #000',
          }}
          styles={{
            title: { color: '#eee' },
            actions: { background: 'transparent'}
          }}
          actions={[
                <Button 
                  type="primary" 
                  danger
                  disabled={!files.length}
                  onClick={async () => {
                    try {
                      setUploading(true);
                      const { hadErrors } = await submitBulkUpload(files, globalMetadata);
                      if (hadErrors) {
                        message.warning("Some uploads failed. See tabs for details.");
                      } else {
                        message.success("All uploads successful!");
                        setFiles([]);
                      }
                    } catch (error) {
                      console.error("Upload failed:", error);
                      message.error("Upload failed. See console for details.");
                    } finally {
                      setUploading(false);
                    }
                  }}
                >
                  {uploading ? <Spin/> : ` Upload ${files.length} Photos`}
                </Button>
          ]}
        >
          <Row justify="center" align="middle" >
            <Col span={24}>
              <UploadDropzone
                onFilesAdded={(newFiles) => {
                  const initialItems = newFiles.map(f => ({
                    ...f,
                    originalSize: f.file.size,
                    progress: 0,
                    status: "compressing",
                  }));
                
                  // Add instantly so tabs render
                  setFiles(prev => [...prev, ...initialItems]);
                
                  // Start background compression
                  initialItems.forEach(startCompression);
                }}
              />
            </Col>
          </Row>
          <Row justify="center" align="middle" style={{ flex: 1, minHeight: 0, marginTop: 20 }}>
            <Col span={24} height="100%" style={{ height: "100%", minHeight: 0 }}>
              <MetadataTabs 
                items={files} 
                setItems={setFiles} 
                globalMetadata={globalMetadata} 
                setGlobalMetadata={setGlobalMetadata} 
              />
            </Col>
          </Row>
      </Card>
      </div>
    </Row>
  );
}

function App() {
  const [auth, setAuth] = useState(null);
  const [tags, setTags] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [locations, setLocations] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [backgroundImages, setBackgroundImages] = useState({
    current: null,
    next: null,
    showNext: false,
  })

  // Count total active filters
  const totalActiveFilters = Object.values(filterOptions).reduce(
    (sum, arr) => sum + (arr?.length || 0), 
    0
  );

  useEffect(() => {
        fetch(`${API_URL}/tags/list/`, {method: "GET"})
          .then((res) => {
            res.json()
              .then(j => setTags(j))
              .catch(console.error)
          })
          .catch(console.error)
        
        fetch(`${API_URL}/subjects/list/`, {method: "GET"})
          .then((res) => {
            res.json()
              .then(j => setSubjects(j))
              .catch(console.error)
          })
          .catch(console.error)

        fetch(`${API_URL}/locations/list/`, {method: "GET"})
          .then((res) => {
            res.json()
              .then(j => setLocations(j))
              .catch(console.error)
          })
          .catch(console.error)
        
        fetch(`${API_URL}/auth/status/`, {method: "GET", credentials: "include"})
          .then((res) => res.json())
          .then(data => setAuth(data));
    },
    []
  )

  const availableFilterOptions = {
    "tags": tags,
    "subject": subjects,
    "location": locations
  }

  const JSXAdminNav = () => {
    if (!auth || !auth.authenticated) {
      return null;
    }
    return (
      <Dropdown menu={{
        items: [
          {
            key: '1',
            label: <a href="/admin/">Admin Dashboard</a>,
          },
          {
            key: '2',
            label: <Link to="/bulk-upload">Bulk Upload Tool</Link>,
          },
          {
            key: '3',
            label: <a href="/admin/logout/">Logout</a>,
          },
        ],
      }}
      >
        <div style={{ color: "#baa", cursor: "pointer" }}>
          <Space>Admin options ({auth?.username}) <DownOutlined /></Space>
          </div>
      </Dropdown>
    )
  }
  
  return (
    <>
    <MorphingBackground {...backgroundImages} />
    <Layout style={{ background: 'transparent', minHeight: '100vh'}}>
      <Header style={{
        backgroundColor: "rgba(30, 30, 30, 0.8)",
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <img 
            src={logo} 
            alt="DCurtis Photo" 
            style={{ 
              height: "40px", 
              width: "auto",
              mixBlendMode: "exclusion",
              cursor: "pointer",
              transition: "opacity 0.2s"
            }}
            onClick={() => setFilterOptions({})}
            onMouseEnter={(e) => e.target.style.opacity = "0.7"}
            onMouseLeave={(e) => e.target.style.opacity = "1"}
            title="Clear all filters"
          />
        </div>
        {auth && auth.authenticated && (
          JSXAdminNav()
        )}
        <Badge count={totalActiveFilters} offset={[-5, 5]}>
          <Button 
            icon={<FilterOutlined />}
            onClick={() => setFilterModalVisible(true)}
            type="primary"
            size="large"
            ghost
            style={{color: "#eee", borderColor: "#eee"}}
          >
            Filters
          </Button>
        </Badge>
        </Header>
      <Content style={{height: "calc(100vh - 64px - 5vh)", overflowX: "hidden", overflowY: "auto", position: "relative"}}>
        <Routes>
          <Route path="/" element={<HomePage setBackgroundImages={setBackgroundImages} filterOptions={filterOptions} />} />
          <Route path="/bulk-upload" element={<BulkUploadPage auth={auth} />} />
        </Routes>
      </Content>
      <Footer style={{
        textAlign: "center",
        height: "5vh",
        backgroundColor: "rgba(10, 10, 10, 0.5)",
        margin: 0,
        padding: 0,
        display: "flex",
        justifyContent: "center",
        columnGap: "1vw"
      }}>
        <Button icon={<InstagramOutlined />} ghost style={{
          color: "#F56040", 
          borderImageSource: "linear-gradient(to left, #5B51D8, #E1306C)", 
          border: "10px rounded", 
          borderImageSlice: 1, 
          borderWidth: "2px", 
          borderRadius: "250px"
        }}
        onClick={() => window.open("https://www.instagram.com/dcurtis_photo")}>
          @dcurtis_photo
        </Button>
        <Button icon={<MailOutlined />} ghost style={{
          color: "#89D3DF", 
          borderImageSource: "linear-gradient(to left, #89D3DF, #63C1A0)", 
          border: "10px rounded", 
          borderImageSlice: 1, 
          borderWidth: "2px", 
          borderRadius: "250px"
        }}>
          dave@dcurtis.co.uk
        </Button>
      </Footer>

      <FilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        options={availableFilterOptions}
        activeFilters={filterOptions}
        onApplyFilters={setFilterOptions}
      />
    </Layout>
    </>
  );
}

export default App;

